/**
 * 파트너 목록 표 — 「화면 설계 › 파트너 목록」.
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면
 */
import React from "react";
import type { PartnerMeta, PartnerSummary, WebhookSecretView } from "api/partnerAdminService";
import { codeLabel, formatDateTime, secretSourceLabel } from "utils/partnerAdmin";
import { EmptyText, Mono, Muted, StatusText, Table, Td, Th, Tr, Warn } from "./style";

/** 서명 키 출처 + 지문. 「없음」은 그 파트너에게 결과가 가지 않는다는 뜻이라 경고로 보인다. */
export const SecretSourceText: React.FC<{ secret: WebhookSecretView | null | undefined }> = ({ secret }) => {
  if (!secret || secret.source === "none") {
    return <Warn data-testid="secret-source-none">없음 · 결과 통지가 가지 않습니다</Warn>;
  }
  return (
    <span>
      {secretSourceLabel(secret.source)}
      {secret.fingerprint && (
        <>
          {" · "}
          <Mono>{secret.fingerprint}</Mono>
        </>
      )}
    </span>
  );
};

interface PartnerTableProps {
  partners: PartnerSummary[];
  meta?: PartnerMeta;
  onRowClick: (partner: PartnerSummary) => void;
}

const PartnerTable: React.FC<PartnerTableProps> = ({ partners, meta, onRowClick }) => {
  if (partners.length === 0) {
    return <EmptyText>등록된 파트너가 없습니다.</EmptyText>;
  }
  return (
    <Table data-testid="partner-table">
      <thead>
        <tr>
          <Th>이름</Th>
          <Th>종류</Th>
          <Th>연결 계정</Th>
          <Th>상태</Th>
          <Th>활성 키</Th>
          <Th>마지막 사용</Th>
          <Th>웹훅 서명 키</Th>
        </tr>
      </thead>
      <tbody>
        {partners.map((p) => (
          <Tr key={p.id} $clickable onClick={() => onRowClick(p)} data-testid={`partner-row-${p.id}`}>
            <Td>{p.name}</Td>
            <Td>{codeLabel(p.code, meta)}</Td>
            <Td>{p.userEmail ?? <Muted>{p.userId}</Muted>}</Td>
            <Td>
              <StatusText $on={p.isActive}>{p.isActive ? "활성" : "비활성"}</StatusText>
            </Td>
            <Td>{p.activeKeyCount}</Td>
            <Td>{formatDateTime(p.lastUsedAt, "사용 전")}</Td>
            <Td>
              <SecretSourceText secret={p.webhookSecret} />
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
};

export default PartnerTable;
